import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { type Lender } from "@shared/schema";
import {
  Building2, Percent, Calculator, TrendingUp, TrendingDown, Minus,
  ChevronRight, AlertTriangle, CheckCircle2, Circle, DollarSign,
  Gauge, Timer, BarChart3, PieChart as PieChartIcon, ArrowRight, Info, Sparkles, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { filterStatementsToLenders, computeFloorplanStats, statementMatchesLender } from "@/lib/floorplanStats";
import { computeRealAprForLender, manualFeePerUnitFromLender } from "@/lib/realApr";
import { intlLocale, fmtDate } from "@/lib/locale";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";

function fmt(v: number, decimals = 2): string {
  return v.toLocaleString(intlLocale(), { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
}
function fmtCurrency(v: number): string {
  return "$" + v.toLocaleString(intlLocale(), { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

// ─── Clickable stat card ────────────────────────────────────────────────────────

function StatCard({
  icon: Icon, label, value, sub, badge, badgeColor, loading, href,
}: {
  icon: any;
  label: string;
  value: string;
  sub?: string;
  badge?: string;
  badgeColor?: "green" | "yellow" | "red" | "muted";
  loading?: boolean;
  href?: string;
}) {
  const colorMap = {
    green: "bg-chart-3/15 text-chart-3 border-chart-3/30",
    yellow: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
    red: "bg-destructive/10 text-destructive border-destructive/30",
    muted: "bg-muted text-muted-foreground border-border",
  };

  const BadgeIcon = badgeColor === "green" ? TrendingUp : badgeColor === "red" ? TrendingDown : Minus;

  const inner = (
    <Card
      data-testid={`dashboard-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={href ? "transition-all duration-150 hover:border-primary/40 hover:shadow-md cursor-pointer group" : ""}
    >
      <CardContent className="pt-5 pb-5 px-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div className="flex items-center gap-2">
            {badge && badgeColor && (
              <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${colorMap[badgeColor]}`}>
                <BadgeIcon className="w-3 h-3" />
                {badge}
              </span>
            )}
            {href && <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />}
          </div>
        </div>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-3.5 w-40" />
          </div>
        ) : (
          <>
            <p className="text-2xl font-bold tracking-tight tabular-nums leading-tight">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1.5 leading-snug">{sub}</p>}
          </>
        )}
        <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wide mt-3">{label}</p>
      </CardContent>
    </Card>
  );

  if (href) return <Link href={href}>{inner}</Link>;
  return inner;
}

// ─── Small metric card (for operational strip) ──────────────────────────────────

function MiniCard({
  icon: Icon, label, value, sub, loading, href, color,
}: {
  icon: any;
  label: string;
  value: string;
  sub?: string;
  loading?: boolean;
  href?: string;
  color?: "blue" | "green" | "amber" | "red";
}) {
  const colorCls = {
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    green: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    red: "bg-red-500/10 text-red-600 dark:text-red-400",
  }[color ?? "blue"];

  const inner = (
    <Card className={href ? "transition-all duration-150 hover:border-primary/40 hover:shadow-md cursor-pointer group" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorCls}`}>
            <Icon className="w-4 h-4" />
          </div>
          {href && <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />}
        </div>
        {loading ? (
          <div className="space-y-1.5">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-3 w-28" />
          </div>
        ) : (
          <>
            <p className="text-xl font-bold tabular-nums">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{sub}</p>}
          </>
        )}
        <p className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide mt-2">{label}</p>
      </CardContent>
    </Card>
  );

  if (href) return <Link href={href}>{inner}</Link>;
  return inner;
}

// ─── APR color helper ────────────────────────────────────────────────────────────

function aprColor(apr: number): string {
  if (apr < 18) return "#22c55e";
  if (apr < 24) return "#f59e0b";
  return "#ef4444";
}

// ─── Custom bar tooltip ──────────────────────────────────────────────────────────

function AprTooltip({ active, payload }: any) {
  const { t } = useTranslation(["dashboard"]);
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold mb-1">{d.name}</p>
      <p className="text-muted-foreground">{t("dashboard:tooltips.effectiveIntRate")} <span className="font-mono font-bold text-foreground">{fmt(d.apr)}%</span></p>
      {d.units > 0 && <p className="text-muted-foreground text-xs mt-0.5">{t("dashboard:tooltips.unitsSampled", { count: d.units })}</p>}
    </div>
  );
}

// ─── Pie tooltip ─────────────────────────────────────────────────────────────────

function PieTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold">{d.name}</p>
      <p className="text-muted-foreground">{fmtCurrency(d.value)}</p>
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────────

function SectionHeader({ title, href }: { title: string; href?: string }) {
  const { t } = useTranslation(["dashboard"]);
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold">{title}</h2>
      {href && (
        <Link href={href} className="flex items-center gap-1 text-xs text-primary hover:underline">
          {t("dashboard:common.viewDetails")} <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

// ─── Empty chart state ────────────────────────────────────────────────────────────

function ChartEmpty({ message, href, linkLabel }: { message: string; href: string; linkLabel: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-[200px] text-center text-muted-foreground gap-3">
      <p className="text-sm">{message}</p>
      <Link href={href} className="text-xs text-primary hover:underline flex items-center gap-1">
        {linkLabel} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { t } = useTranslation(["dashboard"]);
  const [, setLocation] = useLocation();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { data: lenders = [], isLoading: loadingLenders } = useQuery<Lender[]>({ queryKey: ["/api/lenders"] });
  const { data: allStatements = [], isLoading: loadingStmts } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });
  const { data: opAnalytics, isLoading: loadingOp } = useQuery<any>({ queryKey: ["/api/op-analytics/latest"] });
  const { data: onboardingProgress } = useQuery<any>({ queryKey: ["/api/onboarding/progress"], staleTime: 30_000 });

  // Show banner if: not dismissed AND (onboarding not started OR in progress but not complete)
  const onboardingNotStarted = onboardingProgress && !onboardingProgress.progress && !onboardingProgress.profile?.onboardingCompleted;
  const onboardingInProgress = onboardingProgress?.progress && onboardingProgress.progress.currentPhase < 10 && !onboardingProgress.profile?.onboardingCompleted;
  const showOnboardingBanner = !bannerDismissed && !!(onboardingNotStarted || onboardingInProgress);

  const isLoading = loadingLenders || loadingStmts;

  // Only most-recent statement per lender
  const linkedStatements = useMemo(
    () => filterStatementsToLenders(allStatements, lenders),
    [allStatements, lenders],
  );

  // Portfolio stats
  const stats = useMemo(() => computeFloorplanStats(linkedStatements), [linkedStatements]);

  // Effective APR average + per-lender data
  const { avgApr, hasRealData, lenderAprData } = useMemo(() => {
    const vals: number[] = [];
    const lenderData: Array<{ name: string; apr: number; units: number }> = [];

    for (const lender of lenders) {
      const stmts = linkedStatements.filter((s) => statementMatchesLender(s, lender));
      if (!stmts.length) continue;
      const r = computeRealAprForLender(stmts, { manualFeePerUnit: manualFeePerUnitFromLender(lender) });
      if (r.apr !== null && r.apr > 0) {
        vals.push(r.apr);
        lenderData.push({ name: lender.name, apr: Math.round(r.apr * 100) / 100, units: r.totalUnits });
      }
    }
    if (vals.length) return {
      avgApr: vals.reduce((a, b) => a + b, 0) / vals.length,
      hasRealData: true,
      lenderAprData: lenderData,
    };
    return { avgApr: null, hasRealData: false, lenderAprData: [] };
  }, [lenders, linkedStatements]);

  // Aged inventory (units past avg turn threshold)
  const agedData = useMemo(() => {
    const avgDays = stats.avgTurnDays;
    if (!avgDays || avgDays <= 0) return null;
    let aged = 0;
    let totalUnits = 0;
    let totalDaysPastTurn = 0;
    const seenKeys = new Set<string>();
    for (const s of linkedStatements) {
      try {
        const recvs = JSON.parse(s.receivablesJson || "[]");
        for (const r of recvs) {
          const days = r.daysOutstanding ?? r.daysFloored ?? 0;
          if (days > 0) {
            const key = String(r.vin ?? r.stock ?? "").trim().toUpperCase();
            if (key) { if (seenKeys.has(key)) continue; seenKeys.add(key); }
            totalUnits++;
            if (days > avgDays) {
              aged++;
              totalDaysPastTurn += (days - avgDays);
            }
          }
        }
      } catch {}
    }
    if (totalUnits === 0) return null;
    const avgGross = opAnalytics?.input?.avgGrossProfitPerUnit
      ?? opAnalytics?.output?.inputsUsed?.avgGrossProfitPerUnit
      ?? null;
    const revenueAtRisk = avgGross && aged > 0 ? aged * avgGross : null;
    const costPerDay = opAnalytics?.output?.costToCarry?.carryingCostPerCarPerDay ?? null;
    const extraCostToCarry = costPerDay && totalDaysPastTurn > 0 ? costPerDay * totalDaysPastTurn : null;
    return { aged, totalUnits, avgDays, revenueAtRisk, extraCostToCarry };
  }, [linkedStatements, stats.avgTurnDays, opAnalytics]);

  // Interest vs Fees portfolio split
  const feeSplit = useMemo(() => {
    let totalInterest = 0;
    let totalFees = 0;
    for (const s of linkedStatements) {
      totalInterest += parseFloat(s.totalInterestPaid || "0");
      totalFees += parseFloat(s.totalFeesPaid || "0");
    }
    if (totalInterest + totalFees === 0) return null;
    return [
      { name: t("dashboard:charts.seriesInterest"), value: Math.round(totalInterest * 100) / 100 },
      { name: t("dashboard:charts.seriesFees"), value: Math.round(totalFees * 100) / 100 },
    ];
  }, [linkedStatements, t]);

  // Operational analytics
  const opOutput = opAnalytics?.output;
  const opInput = opAnalytics?.input;

  // Data readiness
  const readinessSteps = [
    {
      label: t("dashboard:checklist.lendersAdded"),
      done: lenders.length > 0,
      sub: lenders.length > 0 ? t("dashboard:checklist.lendersConfigured", { count: lenders.length }) : t("dashboard:checklist.lendersAddedEmpty"),
      href: "/floorplan",
      linkLabel: t("dashboard:checklist.goToFloorplan"),
    },
    {
      label: t("dashboard:checklist.statementsUploaded"),
      done: allStatements.length > 0,
      sub: allStatements.length > 0 ? t("dashboard:checklist.statementsOnFile", { count: allStatements.length }) : t("dashboard:checklist.statementsUploadedEmpty"),
      href: "/floorplan",
      linkLabel: t("dashboard:checklist.uploadStatement"),
    },
    {
      label: t("dashboard:checklist.opComputed"),
      done: !!opAnalytics,
      sub: opAnalytics ? t("dashboard:checklist.lastComputed", { date: fmtDate(opAnalytics.lastSavedAt) }) : t("dashboard:checklist.opComputedEmpty"),
      href: "/operations",
      linkLabel: t("dashboard:checklist.goToOperations"),
    },
  ];

  const PIE_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))"];

  return (
    <div className="p-4 md:p-6 space-y-8 max-w-screen-xl">
      {/* Onboarding resume banner */}
      {showOnboardingBanner && (
        <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-primary/5 border border-primary/20" data-testid="onboarding-resume-banner">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">{t("dashboard:onboarding.title")}</p>
              <p className="text-xs text-muted-foreground">
                {onboardingInProgress
                  ? t("dashboard:onboarding.inProgress", { phase: Math.min(onboardingProgress?.progress?.currentPhase ?? 1, 8) })
                  : t("dashboard:onboarding.notStarted")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={() => setLocation("/onboarding")} data-testid="button-resume-onboarding" className="gap-1.5">
              {onboardingInProgress ? t("dashboard:onboarding.resume") : t("dashboard:onboarding.start")} <ArrowRight className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => setBannerDismissed(true)} data-testid="button-dismiss-banner" className="h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("dashboard:header.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("dashboard:header.subtitle")}</p>
      </div>

      {/* ── Top stat cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          label={t("dashboard:stats.activeLenders")}
          value={isLoading ? "—" : String(lenders.length)}
          sub={lenders.length > 0
            ? lenders.map((l) => l.name).join(" · ")
            : t("dashboard:stats.activeLendersEmpty")}
          loading={isLoading}
          href="/floorplan"
        />

        <StatCard
          icon={Percent}
          label={t("dashboard:stats.effectiveIntRate")}
          value={isLoading ? "—" : avgApr !== null ? `${fmt(avgApr)}%` : "—"}
          sub={
            hasRealData
              ? t("dashboard:stats.effectiveIntRateReal")
              : lenders.length > 0
                ? t("dashboard:stats.effectiveIntRateUpload")
                : t("dashboard:stats.effectiveIntRateAddLenders")
          }
          loading={isLoading}
          href="/floorplan"
        />

        <StatCard
          icon={Calculator}
          label=""
          value={t("dashboard:stats.costCalculator")}
          sub={t("dashboard:stats.costCalculatorSub")}
          href="/floorplan#scenario-compare"
        />

        <StatCard
          icon={TrendingUp}
          label=""
          value={t("dashboard:stats.pricingImpact")}
          sub={t("dashboard:stats.pricingImpactSub")}
          href="/floorplan#what-if-pricing"
        />
      </div>

      {/* ── Charts row: APR by Lender + Interest vs Fees ──────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Per-lender APR bar chart — entire card is a link */}
        <Link href="/floorplan" className="lg:col-span-3 block group">
          <Card className="h-full transition-all duration-150 hover:border-primary/40 hover:shadow-md cursor-pointer">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  {t("dashboard:charts.aprByLenderTitle")}
                </CardTitle>
                <span className="flex items-center gap-1 text-xs text-primary">
                  {t("dashboard:common.viewDetails")} <ArrowRight className="w-3 h-3" />
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {lenderAprData.length > 0
                  ? t("dashboard:charts.aprByLenderSubReal")
                  : t("dashboard:charts.aprByLenderSubEmpty")}
              </p>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <Skeleton className="w-full h-full rounded-lg" />
                </div>
              ) : lenderAprData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[200px] text-center text-muted-foreground gap-3">
                  <p className="text-sm">
                    {lenders.length === 0
                      ? t("dashboard:charts.aprEmptyAddLenders")
                      : t("dashboard:charts.aprEmptyUpload")}
                  </p>
                  <span className="text-xs text-primary flex items-center gap-1">
                    {lenders.length === 0 ? t("dashboard:charts.addLenders") : t("dashboard:charts.uploadStatements")} <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={lenderAprData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={42}
                      />
                      <Tooltip content={<AprTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
                      <Bar dataKey="apr" radius={[4, 4, 0, 0]} maxBarSize={64}>
                        {lenderAprData.map((entry, i) => (
                          <Cell key={i} fill={aprColor(entry.apr)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 mt-2 px-1">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#22c55e] shrink-0" />
                      {t("dashboard:charts.legendGood")}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#f59e0b] shrink-0" />
                      {t("dashboard:charts.legendWatch")}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#ef4444] shrink-0" />
                      {t("dashboard:charts.legendHigh")}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Interest vs Fees donut — entire card is a link */}
        <Link href="/floorplan" className="lg:col-span-2 block group">
          <Card className="h-full transition-all duration-150 hover:border-primary/40 hover:shadow-md cursor-pointer">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4 text-primary" />
                  {t("dashboard:charts.interestVsFeesTitle")}
                </CardTitle>
                <span className="flex items-center gap-1 text-xs text-primary">
                  {t("dashboard:common.details")} <ArrowRight className="w-3 h-3" />
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{t("dashboard:charts.interestVsFeesSub")}</p>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <Skeleton className="w-full h-full rounded-lg" />
                </div>
              ) : !feeSplit ? (
                <div className="flex flex-col items-center justify-center h-[200px] text-center text-muted-foreground gap-3">
                  <p className="text-sm">{t("dashboard:charts.interestVsFeesEmpty")}</p>
                  <span className="text-xs text-primary flex items-center gap-1">{t("dashboard:charts.uploadStatements")} <ArrowRight className="w-3 h-3" /></span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={feeSplit}
                      cx="50%"
                      cy="45%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {feeSplit.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                    <Legend
                      formatter={(value, entry: any) => (
                        <span className="text-xs">
                          {value}: {fmtCurrency(entry.payload.value)}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* ── Operational metrics strip ──────────────────────────────────────── */}
      <div>
        <SectionHeader title={t("dashboard:operational.title")} href="/operations" />
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">{t("dashboard:operational.subtitle")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MiniCard
            icon={Gauge}
            label={t("dashboard:operational.costToCarryLabel")}
            value={loadingOp ? "—" : opOutput?.costToCarry?.carryingCostPerCarPerDay
              ? `$${fmt(opOutput.costToCarry.carryingCostPerCarPerDay)}`
              : "—"}
            sub={opOutput?.costToCarry ? t("dashboard:operational.costToCarrySub") : t("dashboard:operational.runToCompute")}
            loading={loadingOp}
            href="/operations"
            color="blue"
          />
          <MiniCard
            icon={Timer}
            label={t("dashboard:operational.breakevenLabel")}
            value={loadingOp ? "—" : opOutput?.breakevenDays?.days
              ? t("dashboard:operational.breakevenValue", { count: opOutput.breakevenDays.days })
              : "—"}
            sub={opOutput?.breakevenDays?.days
              ? t("dashboard:operational.breakevenSub", { day: opOutput.breakevenDays.days })
              : t("dashboard:operational.runToCompute")}
            loading={loadingOp}
            href="/operations"
            color="amber"
          />
          <MiniCard
            icon={TrendingUp}
            label={t("dashboard:operational.avgGrossLabel")}
            value={loadingOp ? "—" : opInput?.avgGrossProfitPerUnit
              ? fmtCurrency(opInput.avgGrossProfitPerUnit)
              : "—"}
            sub={opInput?.avgGrossProfitPerUnit ? t("dashboard:operational.avgGrossSub") : t("dashboard:operational.runToCompute")}
            loading={loadingOp}
            href="/analytics"
            color="green"
          />
        </div>
        {!loadingOp && !opAnalytics && (
          <p className="text-xs text-muted-foreground mt-2">
            {t("dashboard:operational.noAnalyticsPrefix")}{" "}
            <Link href="/operations" className="text-primary hover:underline">{t("dashboard:operational.runOperationalAnalysis")}</Link>{" "}
            {t("dashboard:operational.noAnalyticsSuffix")}
          </p>
        )}
      </div>

      {/* ── Aged inventory alert ───────────────────────────────────────────── */}
      {!isLoading && (
        agedData === null ? (
          /* No receivables data yet */
          <Link href="/floorplan">
            <Card className="border-border hover:border-primary/40 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{t("dashboard:aged.monitorTitle")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("dashboard:aged.monitorSub")}
                    </p>
                  </div>
                  <span className="text-xs text-primary flex items-center gap-1 shrink-0">
                    {t("dashboard:aged.uploadStatements")} <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>
        ) : agedData.aged === 0 ? (
          /* All units within turn target — healthy state */
          <Link href="/floorplan">
            <Card className="border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{t("dashboard:aged.allWithinTarget", { count: agedData.totalUnits, days: agedData.avgDays })}</p>
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400 text-xs">{t("dashboard:aged.onTrack")}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("dashboard:aged.noAgedDetected")}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hidden sm:block" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ) : (
          /* Units past turn target — amber alert */
          <Link href="/floorplan">
            <Card className="border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">
                        {t("dashboard:aged.unitsPastTarget", { count: agedData.aged, days: agedData.avgDays })}
                      </p>
                      <Badge variant="outline" className="border-amber-500/40 text-amber-600 dark:text-amber-400 text-xs">
                        {t("dashboard:aged.agedInventory")}
                      </Badge>
                      <TooltipProvider>
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3.5 h-3.5 text-muted-foreground/60 cursor-default shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[220px] text-xs">
                            {t("dashboard:aged.agedTooltip")}
                          </TooltipContent>
                        </UITooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("dashboard:aged.agedOfTotal", { aged: agedData.aged, total: agedData.totalUnits })}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hidden sm:block" />
                </div>
                {(agedData.revenueAtRisk || agedData.extraCostToCarry) && (
                  <div className="mt-3 pt-3 border-t border-amber-500/20 grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center gap-1">
                        <p className="text-xs text-muted-foreground/70 uppercase tracking-wide font-medium">{t("dashboard:aged.potentialRevenueLost")}</p>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground/50 hover:text-muted-foreground cursor-default shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[240px] text-xs">
                              {t("dashboard:aged.potentialRevenueLostTooltip")}
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-sm font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                        {agedData.revenueAtRisk ? fmtCurrency(agedData.revenueAtRisk) : "—"}
                      </p>
                      <p className="text-xs text-muted-foreground/60">{t("dashboard:aged.avgGrossTimesAged")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground/70 uppercase tracking-wide font-medium">{t("dashboard:aged.extraCostToCarry")}</p>
                      <p className="text-sm font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                        {agedData.extraCostToCarry ? fmtCurrency(agedData.extraCostToCarry) : "—"}
                      </p>
                      <p className="text-xs text-muted-foreground/60">{t("dashboard:aged.carryRateTimesDays")}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        )
      )}

      {/* ── Data readiness checklist ───────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">{t("dashboard:checklist.title")}</CardTitle>
          <p className="text-xs text-muted-foreground">{t("dashboard:checklist.subtitle")}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {readinessSteps.map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              {step.done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              ) : (
                <Circle className="w-5 h-5 text-muted-foreground/40 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${step.done ? "line-through text-muted-foreground" : ""}`}>
                  {step.label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{step.sub}</p>
              </div>
              {!step.done && (
                <Link href={step.href} className="text-xs text-primary hover:underline flex items-center gap-1 shrink-0 mt-0.5">
                  {step.linkLabel} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
